import type { SelectHTMLAttributes, Ref } from 'react';
import { useState, useId } from 'react';
import type { SelectSize, SelectVariant } from './Select.css';
import {
  container,
  containerFullWidth,
  label as labelClass,
  labelUnderline,
  wrapper as wrapperClass,
  wrapperFullWidth,
  selectRecipe,
  animatedBorder,
  animatedBorderFocused,
  errorMessage as errorMessageClass,
} from './Select.css';

export type { SelectSize, SelectVariant };

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Ref */
  ref?: Ref<HTMLSelectElement>;
  /** Select size */
  selectSize?: SelectSize;
  /** 표면 — glass(기본) | flat(운영자 콘솔 절제) */
  variant?: SelectVariant;
  /** Full width */
  fullWidth?: boolean;
  /** Placeholder text (shown as first disabled option) */
  placeholder?: string;
  /** Options */
  options: SelectOption[];
  /** Custom class name */
  className?: string;
  /** Error message */
  error?: string;
  /** Optional label */
  label?: string;
}

export function Select({
  selectSize = 'md',
  variant = 'glass',
  fullWidth = false,
  disabled = false,
  placeholder,
  options,
  className,
  error,
  label,
  onFocus,
  onBlur,
  ref,
  ...props
}: SelectProps) {
  const selectId = useId();
  const errorId = useId();
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    if (!disabled) setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const focused = isFocused && !disabled;

  const selectClass = selectRecipe({
    selectSize,
    isFocused: focused,
    isDisabled: disabled,
    variant,
  });

  return (
    <div className={`${container}${fullWidth ? ` ${containerFullWidth}` : ''}`}>
      {label && (
        <label htmlFor={selectId} className={labelClass}>
          {label}
          {/* 절제(flat)는 그라디언트 0 — 라벨 밑줄 장식을 붙이지 않는다 */}
          {variant !== 'flat' && <span className={labelUnderline} />}
        </label>
      )}
      <div className={`${wrapperClass}${fullWidth ? ` ${wrapperFullWidth}` : ''}`}>
        <div className={`${animatedBorder}${focused ? ` ${animatedBorderFocused}` : ''}`} />
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          data-variant={variant}
          className={`${selectClass}${className ? ` ${className}` : ''}`}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          // ponytail: 보이는 label 이 없는 필터형 select 는 placeholder 를 접근명으로 쓴다. 호출부가 aria-label 을 주면 spread 가 덮는다.
          aria-label={label ? undefined : placeholder}
          {...props}
        >
          {placeholder && (
            <option value="" disabled={false}>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <span id={errorId} role="alert" className={errorMessageClass}>
          {error}
        </span>
      )}
    </div>
  );
}
